import { useCallback, useEffect, useRef, useState } from "react";
import { getWarrantyJourney } from "../common/warrantyApi";

const START = {
  status: "loading",
  data: null,
  events: [],
  nextCursor: null,
  loadingOlder: false,
  olderError: "",
  message: "",
  notFound: false,
};

// One registration's journey, and its activity log paged with the journey's events cursor. A newer
// load drops the answer of an older one, so opening another registration never shows old rows.
export const useWarrantyJourney = (accessToken, registrationId, enabled = true) => {
  const [state, setState] = useState(START);
  const stateRef = useRef(START);
  const seq = useRef(0);
  const mounted = useRef(true);

  const put = useCallback((next) => {
    stateRef.current = typeof next === "function" ? next(stateRef.current) : next;
    setState(stateRef.current);
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    const mine = ++seq.current;
    put({ ...START });
    const result = await getWarrantyJourney(accessToken, registrationId);
    if (!mounted.current || mine !== seq.current) {
      return;
    }
    if (result.ok) {
      put({
        ...START,
        status: "ready",
        data: result.data || {},
        events: Array.isArray(result.data?.events) ? result.data.events : [],
        nextCursor: result.data?.nextEventsCursor || null,
      });
    } else {
      put({
        ...START,
        status: "error",
        message: result.message,
        notFound: result.notFound === true || result.code === "REGISTRATION_NOT_FOUND",
      });
    }
  }, [accessToken, registrationId, put]);

  useEffect(() => {
    if (enabled && registrationId) {
      reload();
    }
  }, [enabled, registrationId, reload]);

  const loadOlder = useCallback(async () => {
    const cursor = stateRef.current.nextCursor;
    if (!cursor || stateRef.current.loadingOlder) {
      return;
    }
    const mine = seq.current;
    put((prev) => ({ ...prev, loadingOlder: true, olderError: "" }));
    const result = await getWarrantyJourney(accessToken, registrationId, cursor);
    if (!mounted.current || mine !== seq.current) {
      return;
    }
    if (result.ok) {
      put((prev) => ({
        ...prev,
        loadingOlder: false,
        events: [...prev.events, ...(Array.isArray(result.data?.events) ? result.data.events : [])],
        nextCursor: result.data?.nextEventsCursor || null,
      }));
    } else {
      put((prev) => ({ ...prev, loadingOlder: false, olderError: result.message }));
    }
  }, [accessToken, registrationId, put]);

  return { ...state, reload, loadOlder };
};
